/**
 * Cấu hình của module eKYC: quy trình định danh, ứng dụng, bí mật webhook, lượt giao webhook (ADR-0085).
 *
 * Gọi API thật khi đăng nhập Keycloak thật, dùng `src/mock/ekyc-cau-hinh.ts` khi chưa (`dungApiThat()`).
 *
 * ## Hai lời gọi trả bí mật
 *
 * `taoUngDung()` và `xoayBiMat()` mang `webhookSecret` — **lần duy nhất** bí mật đi ra khỏi máy chủ. Phía gọi giữ nó
 * trong state của hộp thoại đúng một lần hiện, **không** đưa vào cache của react-query: cache sống qua lượt điều hướng,
 * và một bí mật nằm trong bộ nhớ trang lâu hơn cái hộp hiện nó là một bản sao không ai biết.
 */
import { dungApiThat, goiApi } from '../api/http';
import { mayChuCauHinhEkyc } from '../../mock/ekyc-cau-hinh';
import { mockRequest } from '../../mock/transport';
import type {
  ChinhSach,
  DanhMucQuyTrinh,
  DanhSachUngDung,
  LuotGiaoWebhook,
  QuyTrinh,
  QuyTrinhSua,
  QuyTrinhTao,
  SuDungUngDung,
  TrangThaiGiao,
  UngDung,
  UngDungCoBiMat,
  UngDungSua,
  UngDungTao,
  XemTruocQuyTrinh,
} from '../../modules/ekyc/types';

const GOC = '/api/v1/m/ekyc';
const ma = (id: string) => encodeURIComponent(id);

/** Chạy máy chủ giả **sau** nhịp chờ và các trạng thái `?mock=`: lỗi nghiệp vụ tới cùng độ trễ với lỗi thật. */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

export const ekycCauHinhRepository = {
  // ----- Quy trình ------------------------------------------------------------------------------------------------

  /** Danh mục bước và lựa chọn của chính sách. Giao diện không chép luật của máy chủ — nó đọc ở đây. */
  async danhMuc(): Promise<DanhMucQuyTrinh> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.danhMuc());
    return goiApi<DanhMucQuyTrinh>(`${GOC}/quy-trinh/danh-muc`);
  },

  async dsQuyTrinh(ungDungId: string): Promise<{ items: QuyTrinh[] }> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.dsQuyTrinh(ungDungId));
    return goiApi<{ items: QuyTrinh[] }>(`${GOC}/quy-trinh`, { query: { ungDungId } });
  },

  /** Xem trước phía server: bản hiệu lực và vấn đề của một cấu hình chưa lưu. Không ghi gì. */
  async xemTruoc(buoc: Record<string, boolean>, chinhSach: ChinhSach): Promise<XemTruocQuyTrinh> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.xemTruoc(buoc, chinhSach));
    return goiApi<XemTruocQuyTrinh>(`${GOC}/quy-trinh/xem-truoc`, { method: 'POST', body: { buoc, chinhSach } });
  },

  async taoQuyTrinh(du: QuyTrinhTao): Promise<QuyTrinh> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.taoQuyTrinh(du));
    return goiApi<QuyTrinh>(`${GOC}/quy-trinh`, { method: 'POST', body: du });
  },

  /** `phienBan` là khoá lạc quan: lệch thì 409 `EKYC_WORKFLOW_STALE`, không ghi đè lượt lưu của người khác. */
  async suaQuyTrinh(id: string, du: QuyTrinhSua): Promise<QuyTrinh> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.suaQuyTrinh(id, du));
    return goiApi<QuyTrinh>(`${GOC}/quy-trinh/${ma(id)}`, { method: 'PATCH', body: du });
  },

  async datMacDinh(id: string): Promise<QuyTrinh> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.datMacDinh(id));
    return goiApi<QuyTrinh>(`${GOC}/quy-trinh/${ma(id)}/mac-dinh`, { method: 'POST' });
  },

  async xoaQuyTrinh(id: string): Promise<void> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.xoaQuyTrinh(id));
    await goiApi<void>(`${GOC}/quy-trinh/${ma(id)}`, { method: 'DELETE' });
  },

  // ----- Ứng dụng -------------------------------------------------------------------------------------------------

  async dsUngDung(): Promise<DanhSachUngDung> {
    if (!dungApiThat()) return mockRequest(mayChuCauHinhEkyc.dsUngDung(), { items: [], coKhoaGoc: true });
    return goiApi<DanhSachUngDung>(`${GOC}/ung-dung`);
  },

  async suDung(id: string): Promise<SuDungUngDung> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.suDung(id));
    return goiApi<SuDungUngDung>(`${GOC}/ung-dung/${ma(id)}/su-dung`);
  },

  /** ⚠️ Phản hồi mang bí mật webhook — xem phần đầu tệp. */
  async taoUngDung(du: UngDungTao): Promise<UngDungCoBiMat> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.taoUngDung(du));
    return goiApi<UngDungCoBiMat>(`${GOC}/ung-dung`, { method: 'POST', body: du });
  },

  async suaUngDung(id: string, du: UngDungSua): Promise<UngDung> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.suaUngDung(id, du));
    return goiApi<UngDung>(`${GOC}/ung-dung/${ma(id)}`, { method: 'PATCH', body: du });
  },

  /** ⚠️ Phản hồi mang bí mật webhook mới; bí mật cũ hết hiệu lực ngay. */
  async xoayBiMat(id: string): Promise<UngDungCoBiMat> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.xoayBiMat(id));
    return goiApi<UngDungCoBiMat>(`${GOC}/ung-dung/${ma(id)}/xoay-bi-mat`, { method: 'POST' });
  },

  async batTat(id: string, bat: boolean): Promise<UngDung> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.batTat(id, bat));
    return goiApi<UngDung>(`${GOC}/ung-dung/${ma(id)}/${bat ? 'bat' : 'tat'}`, { method: 'POST' });
  },

  // ----- Webhook --------------------------------------------------------------------------------------------------

  async luotGiao(ungDungId: string, trangThai?: TrangThaiGiao): Promise<{ items: LuotGiaoWebhook[] }> {
    if (!dungApiThat()) return mockRequest(mayChuCauHinhEkyc.luotGiao(ungDungId), { items: [] });
    return goiApi<{ items: LuotGiaoWebhook[] }>(`${GOC}/ung-dung/${ma(ungDungId)}/gui-webhook`, {
      query: { trangThai, limit: 50 },
    });
  },

  /** Gửi lại tay lượt đã hết lượt thử: đúng một lần thử nữa. */
  async thuLaiWebhook(id: string): Promise<LuotGiaoWebhook> {
    if (!dungApiThat()) return giaLap(() => mayChuCauHinhEkyc.thuLai(id));
    return goiApi<LuotGiaoWebhook>(`${GOC}/gui-webhook/${ma(id)}/thu-lai`, { method: 'POST' });
  },
};
